import React, { useState, useRef, useEffect } from 'react';
import { LanguageOption } from '../../types';
import { translatePathForLocale } from '../../utils/navigation';

interface LanguageSwitcherProps {
  options: LanguageOption[];
  currentLanguage: string;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  options,
  currentLanguage,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<LanguageOption[]>(options);
  const [actualCurrentLanguage, setActualCurrentLanguage] = useState(currentLanguage);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Get actual current language from DOM
  useEffect(() => {
    const getActualCurrentLanguage = () => {
      if (typeof document !== 'undefined') {
        const body = document.body;
        if (body && body.dataset.locale) {
          setActualCurrentLanguage(body.dataset.locale);
        }
      }
    };

    // Try immediately
    getActualCurrentLanguage();

    // Also try after a short delay in case DOM isn't ready
    const timeoutId = setTimeout(getActualCurrentLanguage, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Fetch available locales from API
  useEffect(() => {
    const fetchAvailableLocales = async () => {
      try {
        const response = await fetch('/api/locales');
        if (response.ok) {
          const data = await response.json();
          if (data.locales && Array.isArray(data.locales)) {
            // Filter options to only include available locales
            const filtered = options.filter(option => data.locales.includes(option.code));
            setFilteredOptions(filtered);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch available locales, using all provided options:', error);
        // Fallback to using all provided options
        setFilteredOptions(options);
      }
    };

    fetchAvailableLocales();
  }, [options]);
  
  const currentOption = filteredOptions.find(option => option.code === actualCurrentLanguage);

  const handleLanguageChange = (locale: string) => {
    if (locale === actualCurrentLanguage) {
      return;
    }
    const { pathname, search, hash } = window.location;
    const newPath = translatePathForLocale(pathname, locale) + (search || '') + (hash || '');
    window.location.href = newPath;
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-colors rounded-md"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="uppercase font-semibold">{currentOption?.code || actualCurrentLanguage || 'LV'}</span>
        <svg 
          className={`ml-1 h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 z-50 w-24 mt-2 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="language-menu-button">
            {filteredOptions.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLanguageChange(option.code);
                  setIsOpen(false);
                }}
                className={`
                  block px-4 py-2 text-sm w-full text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200 uppercase font-semibold
                  ${option.code === actualCurrentLanguage ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}
                `}
                role="menuitem"
              >
                {option.code}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
